import { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { apiFetch } from '../../config/api'

export default function DashboardLogin() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [dbTestResult, setDbTestResult] = useState<string | null>(null)
  const [isTestingDb, setIsTestingDb] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email || !password) {
      setError('Bitte füllen Sie alle Felder aus.')
      return
    }

    try {
      const response = await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        localStorage.setItem('dashboard_token', data.token)
        localStorage.setItem('dashboard_user', JSON.stringify(data.user))
        navigate('/dashboard')
      } else {
        setError(data.error || 'Anmeldung fehlgeschlagen')
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte versuchen Sie es erneut.')
      console.error('Login error:', err)
    }
  }

  const testDatabaseConnection = async () => {
    setIsTestingDb(true)
    setDbTestResult(null)
    setError('')

    try {
      // Versuche zuerst direkt test-db.php
      const apiBase = import.meta.env.VITE_API_BASE_URL || '/api'
      const testUrl = apiBase.startsWith('http') 
        ? `${apiBase}/test-db.php`
        : `${apiBase}/test-db.php`
      
      console.log('Teste URL:', testUrl)
      
      const response = await fetch(testUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      console.log('Response Status:', response.status)
      console.log('Response URL:', response.url)

      // Prüfe ob Response Text ist (PHP-Fehler)
      const responseText = await response.text()
      
      // Wenn HTML/PHP-Code zurückkommt, ist PHP nicht ausgeführt worden
      if (responseText.trim().startsWith('<?php') || responseText.trim().startsWith('<!')) {
        throw new Error(`PHP wird nicht ausgeführt. Die Datei wird direkt geladen statt über den Router.\n\nAntwort (erste 300 Zeichen):\n${responseText.substring(0, 300)}`)
      }
      
      let data
      try {
        data = JSON.parse(responseText)
      } catch (parseError) {
        throw new Error(`Ungültige JSON-Antwort: ${responseText.substring(0, 200)}`)
      }

      if (data.success) {
        const result = `
✅ Verbindung erfolgreich!

Datenbank: ${data.database}
Host: ${data.host}

Tabellen:
${Object.entries(data.tables).map(([table, info]: [string, any]) => 
  `  ${table}: ${info.exists ? `✅ ${info.count} Einträge` : '❌ Nicht gefunden'}`
).join('\n')}

Admin-User: ${data.admin_user_exists ? '✅ Existiert' : '❌ Nicht gefunden'}
${data.admin_user ? `  ID: ${data.admin_user.id}\n  E-Mail: ${data.admin_user.email}` : ''}
        `.trim()
        setDbTestResult(result)
      } else {
        setDbTestResult(`❌ Fehler: ${data.message || data.error}\n\nDetails:\n${JSON.stringify(data, null, 2)}`)
      }
    } catch (err: any) {
      // Versuche direkten Test-Endpoint
      try {
        const testResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}/test-connection.php`)
        const testText = await testResponse.text()
        if (testText.includes('success')) {
          const testData = JSON.parse(testText)
          setDbTestResult(`✅ API ist erreichbar, aber /test-db Endpoint nicht gefunden.\n\nAPI Info:\n${JSON.stringify(testData, null, 2)}\n\nMögliche Ursachen:\n- Router.php leitet nicht richtig weiter\n- test-db.php fehlt im api/ Ordner\n- .htaccess Konfiguration`)
        } else {
          throw new Error('Test-Endpoint auch nicht erreichbar')
        }
      } catch (testErr) {
        setDbTestResult(`❌ Verbindungsfehler: ${err.message || 'Unbekannter Fehler'}\n\nAufgerufene URL: ${err.url || 'unbekannt'}\n\nBitte prüfe:\n- Ist die API-URL korrekt? (aktuell: ${import.meta.env.VITE_API_BASE_URL || '/api'})\n- Ist die .htaccess korrekt?\n- Läuft der Backend-Server?\n- Ist router.php vorhanden?`)
      }
      console.error('DB Test error:', err)
    } finally {
      setIsTestingDb(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-900 to-gray-900 px-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-brand-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-brand-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="relative bg-zinc-950/95 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/10 p-10 w-full max-w-md overflow-hidden"
      >
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />
        
        <div className="relative z-10">
          {/* Logo Section */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-6 flex justify-center"
            >
              <img 
                src="/Logo_HM.png" 
                alt="Hückels May Logo" 
                className="h-24 w-auto drop-shadow-2xl"
              />
            </motion.div>
            <h1 className="font-serif text-4xl font-light text-white mb-2">
              Dashboard
            </h1>
            <p className="text-gray-300 text-sm mb-4">Willkommen zurück</p>
            <div className="h-px w-24 bg-gradient-to-r from-brand-500 to-brand-500 mx-auto" />
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
                <svg className="w-4 h-4 text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
                E-Mail
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500/50 focus:bg-white/10 transition-all duration-300 outline-none hover:border-white/20"
                placeholder="ihre@email.de"
                required
              />
            </motion.div>
            
            {/* Password Field */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <label htmlFor="password" className="block text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
                <svg className="w-4 h-4 text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Passwort
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500/50 focus:bg-white/10 transition-all duration-300 outline-none hover:border-white/20"
                placeholder="••••••••"
                required
              />
            </motion.div>
            
            {/* Error Message */}
            {error && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="flex items-start gap-3 text-red-400 text-sm bg-red-500/10 border border-red-500/30 p-4 rounded-xl"
              >
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </motion.div>
            )}
            
            {/* Submit Button */}
            <motion.button
              type="submit"
              className="w-full bg-gradient-to-r from-brand-600 to-brand-600 border border-brand-500/30 text-white py-4 rounded-xl hover:from-brand-500 hover:to-brand-500 transition-all duration-300 font-semibold shadow-xl shadow-brand-600/30 hover:shadow-brand-500/50 relative overflow-hidden group"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              {/* Button shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              <span className="relative flex items-center justify-center gap-2">
                Anmelden
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
            </motion.button>
          </form>

          {/* Database Test Section */}
          <motion.div 
            className="mt-8 pt-8 border-t border-white/10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <motion.button
              type="button"
              onClick={testDatabaseConnection}
              disabled={isTestingDb}
              className="w-full bg-white/5 border border-white/10 text-gray-300 py-4 rounded-xl hover:bg-white/10 hover:border-white/20 transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 group relative overflow-hidden"
              whileHover={{ scale: isTestingDb ? 1 : 1.02 }}
              whileTap={{ scale: isTestingDb ? 1 : 0.98 }}
            >
              {isTestingDb ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-brand-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Teste Verbindung...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 text-brand-400 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Datenbankverbindung testen
                </>
              )}
            </motion.button>

            {dbTestResult && (
              <motion.div 
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ type: "spring", stiffness: 100 }}
                className="mt-5 p-5 bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm"
              >
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="font-semibold text-base text-white">Test-Ergebnis</h3>
                </div>
                <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono overflow-auto max-h-80 bg-black/30 p-4 rounded-lg border border-white/5 leading-relaxed">
                  {dbTestResult}
                </pre>
              </motion.div>
            )}
          </motion.div>
        </div>
      </motion.div>
    </div>
  )
}
















